// src/hooks/useConferenciaKanban.ts
import { useCallback, useState } from 'react';
import { apiGet } from '../services/api';
import { usePaginatedFetch } from './usePaginatedFetch';
import { OrigemConferencia } from '../constants';

export interface RecebimentoPendenteItem {
  id: number;
  dataCriacao: string;
  status: string;
  usuarioId: number;
  codFornecedor: number;
  nomeFornecedor: string;
  nomeUsuario: string;
  countDiasAberto: number;
  dataEncerrada: string;
  countItens: number;
  dataEntrega: string;
  lojaEntregaId: number;
  nomeLojaEntrega: string;
  origem?: OrigemConferencia;
}

export interface ConferenciaRascunhoItem {
  id: number;
  dataCriacao: string;
  status: string;
  usuarioId: number;
  nomeUsuario: string;
  lojaEntregaId: number;
  nomeLojaEntrega: string;
  countItens: number;
  countDiasAberto: number;
  mensagemFornecedores: string;
  nroNotaFiscal: number | null;
  codFornecedor: number;
  nomeFornecedor: string;
}

export interface ConferenciaFinalizadoItem {
  id: number;
  dataCriacao: string;
  status: string;
  usuarioId: number;
  nomeUsuario: string;
  lojaEntregaId: number;
  nomeLojaEntrega: string;
  countItens: number;
  countDiasAberto: number;
  mensagemFornecedores: string;
  nroNotaFiscal: number | null;
  codFornecedor: number;
  nomeFornecedor: string;
  dataEncerrada: string;
}

export const useConferenciaKanban = () => {
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [allIdsRascunho, setAllIdsRascunho] = useState<number[]>([]);
  const [allIdsFinalizado, setAllIdsFinalizado] = useState<number[]>([]);

  const fetchPendentes = useCallback(async (pageNumber: number) => {
    if (isSearchActive && searchQuery) {
      const res = await apiGet(`/v1/busca-inteligente/capa-gestao-conferencia?${searchQuery}&page=${pageNumber}&size=10`);
      const section = res.data?.pendente;
      const items: RecebimentoPendenteItem[] = section?.content || [];
      return { items, totalItems: section?.page?.totalElements || 0 };
    }
    const res = await apiGet(`/v1/recebimento-conferencia/compra-distribuicao/capa?page=${pageNumber}&size=10&status=ABERTA`);
    return { items: res.data.content as RecebimentoPendenteItem[], totalItems: res.data.page.totalElements };
  }, [isSearchActive, searchQuery]);

  const fetchRascunho = useCallback(async (pageNumber: number) => {
    if (isSearchActive && searchQuery) {
      const res = await apiGet(`/v1/busca-inteligente/capa-gestao-conferencia?${searchQuery}&page=${pageNumber}&size=10`);
      const section = res.data?.rascunho;
      const items: ConferenciaRascunhoItem[] = section?.content || [];
      if (pageNumber === 0) {
        if (Array.isArray(section?.allIds) && section.allIds.length) setAllIdsRascunho(section.allIds);
        else setAllIdsRascunho(items.map((i) => i.id));
      }
      return { items, totalItems: section?.page?.totalElements || 0 };
    }
    const res = await apiGet(`/v1/recebimento-conferencia/capa?page=${pageNumber}&size=10`);
    const items: ConferenciaRascunhoItem[] = res.data.content;
    if (pageNumber === 0) {
      if (Array.isArray(res.data.allIds) && res.data.allIds.length) setAllIdsRascunho(res.data.allIds);
      else setAllIdsRascunho(items.map((i) => i.id));
    }
    return { items, totalItems: res.data.page.totalElements };
  }, [isSearchActive, searchQuery]);

  const fetchFinalizados = useCallback(async (pageNumber: number) => {
    if (isSearchActive && searchQuery) {
      const res = await apiGet(`/v1/busca-inteligente/capa-gestao-conferencia?${searchQuery}&page=${pageNumber}&size=10`);
      const section = res.data?.finalizado;
      const items: ConferenciaFinalizadoItem[] = section?.content || [];
      if (pageNumber === 0) {
        if (Array.isArray(section?.allIds) && section.allIds.length) setAllIdsFinalizado(section.allIds);
        else setAllIdsFinalizado(items.map((i) => i.id));
      }
      return { items, totalItems: section?.page?.totalElements || 0 };
    }
    const res = await apiGet(`/v1/recebimento-conferencia/capa?page=${pageNumber}&size=10&status=FINALIZADO`);
    const items: ConferenciaFinalizadoItem[] = res.data.content;
    if (pageNumber === 0) {
      if (Array.isArray(res.data.allIds) && res.data.allIds.length) setAllIdsFinalizado(res.data.allIds);
      else setAllIdsFinalizado(items.map((i) => i.id));
    }
    return { items, totalItems: res.data.page.totalElements };
  }, [isSearchActive, searchQuery]);

  const pendentes = usePaginatedFetch<RecebimentoPendenteItem>({
    fetchFunction: fetchPendentes,
    pageSize: 10,
    uniqueKey: 'id',
  });

  const rascunho = usePaginatedFetch<ConferenciaRascunhoItem>({
    fetchFunction: fetchRascunho,
    pageSize: 10,
    uniqueKey: 'id',
  });

  const finalizados = usePaginatedFetch<ConferenciaFinalizadoItem>({
    fetchFunction: fetchFinalizados,
    pageSize: 10,
    uniqueKey: 'id',
  });

  // Refetch on search change é gerenciado pelo próprio usePaginatedFetch:
  // fetchXxx tem deps [isSearchActive, searchQuery] → loadData muda → reset muda
  // → useEffect interno [reset, enabled] dispara refetch automaticamente.

  const reloadAllColumns = () => {
    pendentes.reset();
    rascunho.reset();
    finalizados.reset();
  };

  const handleSearch = (query: string) => {
    setIsSearchActive(true);
    setSearchQuery(query);
  };

  const handleResetSearch = () => {
    setIsSearchActive(false);
    setSearchQuery('');
    setAllIdsRascunho([]);
    setAllIdsFinalizado([]);
  };

  return {
    pendentes,
    rascunho,
    finalizados,
    isSearchActive,
    allIdsRascunho,
    allIdsFinalizado,
    handleSearch,
    handleResetSearch,
    reloadAllColumns,
  };
};
